import React, {
  FC,
  useContext,
} from 'react';
import clsx from 'clsx';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ConsentFormIcon from 'core/icons/consentForm';
import CommentsIcon from 'core/icons/comments';
import DemographicsIcon from 'core/icons/demographics';
import InsuranceIcon from 'core/icons/insurance';
import AuthorizationIcon from 'core/icons/authorization';
import Authorizations from 'views/EMR/components/Authorizations';
import LedgerIcon from 'core/icons/ledger';
import PatientHistoryIcon from 'core/icons/patientHistory';
import PatientReviewIcon from 'core/icons/patientReview';
import VisitIcon from 'core/icons/visit';
import MedicationsIcon from 'core/icons/medications';
import VitalsIcon from 'core/icons/vitals';
import Demographics from 'pages/Dashboard/pages/Charts/pages/Details/components/Demographics';
import PCC from 'pages/Dashboard/pages/Charts/pages/Details/components/PCC';
import Ledger from 'pages/Dashboard/pages/Charts/pages/Details/components/Ledger';
import Medications from 'pages/Dashboard/pages/Charts/pages/Details/components/Medications';
import Vitals from 'pages/Dashboard/pages/Charts/pages/Details/components/Vitals';
import Multimedia from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia';
import PatientHistory from 'pages/Dashboard/pages/Charts/pages/Details/components/PatientHistory';
import VisitAndNotes from 'pages/Dashboard/pages/Charts/pages/Details/components/VisitsAndNotes';
import {
  Props,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import ReviewOfSystems from 'pages/Dashboard/pages/Charts/pages/Details/components/ReviewOfSystems';
import InsuranceList from 'views/EMR/components/Insurance/InsuranceList';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import ToolTip from 'core/components/Tooltip';
import {
  ChartTab,
  getPatientChartPath,
  getValidChartTab,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  usePCCAvailability,
} from 'views/EMR/hooks/pcc';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

const iconClassName = '!w-4 !h-4';
const tabClassName = '!min-h-[48px] !normal-case !font-semibold';

type ChartSection = {
  Icon: typeof DemographicsIcon;
  value: ChartTab;
  label: string;
};

export const sections: ChartSection[] = [{
  Icon: DemographicsIcon,
  value: 'demographics',
  label: 'Demographics',
}, {
  Icon: CommentsIcon,
  value: 'pcc',
  label: 'PCC',
}, {
  Icon: VisitIcon,
  value: 'visits',
  label: 'Visits',
}, {
  Icon: LedgerIcon,
  value: 'ledger',
  label: 'Ledger',
}, {
  Icon: InsuranceIcon,
  value: 'insurance',
  label: 'Insurance',
}, {
  Icon: AuthorizationIcon,
  value: 'authorizations',
  label: 'Authorizations',
}, {
  Icon: MedicationsIcon,
  value: 'medications-and-allergies',
  label: 'Meds & Allergies',
}, {
  Icon: VitalsIcon,
  value: 'vitals',
  label: 'Vitals',
}, {
  Icon: PatientHistoryIcon,
  value: 'patient-history',
  label: 'Patient\'s History',
}, {
  Icon: PatientReviewIcon,
  value: 'review-of-systems',
  label: 'Review of Systems',
}, {
  Icon: ConsentFormIcon,
  value: 'multimedia',
  label: 'Multimedia',
}];

export const keyedComponents: Record<string, FC<Props>> = {
  demographics: Demographics,
  pcc: PCC,
  visits: VisitAndNotes,
  vitals: Vitals,
  ledger: Ledger,
  insurance: InsuranceList,
  authorizations: Authorizations,
  'medications-and-allergies': Medications,
  multimedia: Multimedia,
  'patient-history': PatientHistory,
  'review-of-systems': ReviewOfSystems,
};

export default function Navigation() {
  const {
    isPanel,
    id,
    tabValue,
    navigate,
  } = useContext(ViewContext);
  const {
    canChartTab,
    isRestricted: hasRestrictedAccess,
  } = useEMRAccess();
  const { isPCCAvailable } = usePCCAvailability();
  const visibleSections = sections.filter(
    (section) => (
      (hasRestrictedAccess || section.value !== 'pcc' || isPCCAvailable)
      && canChartTab(section.value)
    ),
  );
  const activeTab = getValidChartTab(tabValue);

  return (
    <Tabs
      className={clsx(
        'populate-route-tabs !min-h-[48px] w-full border-b border-b-gray-200 px-4 nw-charts-navigation',
        { 'populate-panel-navigation': isPanel },
      )}
      value={isPanel ? activeTab : getPatientChartPath(id, activeTab)}
      onChange={(event, value: string) => { navigate(value); }}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
    >
      {visibleSections.map(({ Icon, ...tabOptions }) => (
        <Tab
          key={tabOptions.value}
          className={clsx(tabClassName, {
            '!p-2': !isPanel,
            '!p-1 !min-w-[64px]': isPanel,
          })}
          iconPosition="start"
          {...tabOptions}
          label={isPanel ? null : tabOptions.label}
          value={isPanel ? tabOptions.value : getPatientChartPath(id, tabOptions.value)}
          icon={(
            <ToolTip
              title={tabOptions.label}
              hide={!isPanel}
            >
              <div className="populate-icon-wrapper mr-2">
                <Icon className={iconClassName} />
              </div>
            </ToolTip>
          )}
          data-nw-attr={tabOptions.value}
        />
      ))}
    </Tabs>
  );
}
